import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  buttonClasses,
} from '@mui/material';
import React, { FC, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { GoogleIcon } from '../../components/icons';
import TitledLogo from '../../components/TitledLogo';
import { RootState } from '../../store';
import {
  appViewFetchUserRequest,
  getAppViewUser,
  appViewSetUser,
} from '../../app/store/appViewSlice';
import { authService } from '../../services/authService';

const Copyright: FC = () => {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{
        mt: 5,
        position: 'absolute',
        bottom: ({ spacing }) => spacing(3),
        left: '50%',
        transform: 'translate(-50%, 0)',
      }}
    >
      {'Copyright © '}
      <Link color="inherit" to="https://heshbonita.com/">
        Heshbonita
      </Link>{' '}
      {new Date().getFullYear()}
    </Typography>
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const Login: FC<Props> = ({ user, onFetchUser, dispatch }) => {
  const navigate = useNavigate();

  // Handle the OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if we're in a callback scenario
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get('code');

      if (code) {
        try {
          console.log(
            'Received code parameter in URL, handling OAuth callback',
          );

          // Process the auth callback through our service
          const authResponse =
            await authService.handleAuthCallback(searchParams);
          console.log(
            'Auth response received:',
            JSON.stringify({
              token: authResponse.token ? 'present' : 'missing',
              user: authResponse.user ? 'present' : 'missing',
            }),
          );

          // Store the token securely
          authService.storeToken(authResponse.token);

          // Update user data in Redux store
          dispatch(
            appViewSetUser({
              first_name: authResponse.user.firstName,
              last_name: authResponse.user.lastName,
              email: authResponse.user.email,
              image: '', // Set a default or placeholder image
            }),
          );

          // Clear the URL parameters
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );

          // Redirect to home page or dashboard
          navigate('/');

          // If new user - show a welcome message or tutorial prompt
          if (authResponse.user.isNewUser) {
            // TODO: Consider showing an onboarding message or tutorial
            console.log(
              'Welcome new user! Initial scan is running in the background.',
            );
          }
        } catch (error) {
          console.error('Failed to handle OAuth callback:', error);
          // TODO: Show error message to user
        }
      }
    };

    handleOAuthCallback();
  }, [navigate, dispatch]);

  // Check for existing auth token on load
  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getToken();

      if (token) {
        try {
          // Fetch the user profile using the stored token
          const userProfile = await authService.fetchUserProfile();

          if (userProfile) {
            // Update Redux store with user data
            onFetchUser(userProfile.email);

            // Redirect to home or dashboard if already authenticated
            navigate('/');
          }
        } catch (error) {
          console.error('Failed to validate existing authentication:', error);
          // If token validation fails, clear it
          authService.clearToken();
        }
      }
    };

    checkAuth();
  }, [onFetchUser, navigate]);

  const onAuthUserLogin = async () => {
    try {
      // Get auth URL from the service and redirect
      const authUrl = await authService.getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      // TODO: Show error message to user
    }
  };

  return (
    <Grid
      container
      component="main"
      sx={{ height: '100vh', flexDirection: 'row-reverse' }}
    >
      <Grid
        item
        xs={false}
        sm={4}
        md={8}
        sx={{
          backgroundImage: 'url(https://picsum.photos/3840/2160?grayscale)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: ({ palette }) =>
            palette.mode === 'light' ? palette.grey[50] : palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={4} component={Paper} elevation={6} square>
        <Box
          height="100%"
          position="relative"
          sx={{
            px: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <TitledLogo gap={1} logoHeight={4} titleFontSize={3.5} />
          <Typography fontWeight={600} sx={{ opacity: 0.8 }}>
            זה כסף, קל
          </Typography>
          <Typography fontSize={22} fontWeight={300} sx={{ mt: 2 }}>
            אנחנו מכירים?{' '}
          </Typography>
          <Box component="form" noValidate sx={{ width: '100%' }}>
            <Button
              fullWidth
              onClick={onAuthUserLogin}
              variant="outlined"
              sx={{
                textTransform: 'none',
                mt: 2,
                mb: 2,
                width: '100%',
                [`& .${buttonClasses.endIcon}`]: {
                  marginRight: ({ spacing }) => spacing(0.75),
                },
              }}
              endIcon={<GoogleIcon fontSize="large" />}
            >
              {' '}
              היכנס עם{' '}
            </Button>
            <Grid container></Grid>
            <Copyright />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: getAppViewUser(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onFetchUser: (email: string) => dispatch(appViewFetchUserRequest(email)),
  dispatch: dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
